import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  isAnalyticsConfigured,
  isProductionAnalyticsHost,
  initAnalytics,
  consentDecided,
  analyticsOptedIn,
  acceptAnalytics,
  declineAnalytics,
  capturePageview,
  identifyUser,
  groupOrg,
  resetAnalytics,
} from '../../lib/analytics/posthog';
import { AnalyticsConsentBanner } from './AnalyticsConsentBanner';

/**
 * Wires PostHog into the app (OMN-360): initialises it (capturing off until
 * consent), shows the consent banner, and — only once the user has opted in —
 * captures SPA pageviews and identifies the signed-in user pseudonymously
 * (uid + role) grouped by organization. Must render inside Router + Auth +
 * Organization + Language providers.
 *
 * Nothing is sent to PostHog before opt-in: calling identify/group while opted
 * out would seed PostHog's local state and get dropped, so the `enabled` gate
 * holds all of it until consent is granted.
 */
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { organization } = useOrganization();
  const [showBanner, setShowBanner] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // Initialise once; decide whether the banner is needed and whether a prior
  // "accepted" choice means we can start capturing immediately.
  //
  // Gated to the production host: dev deploys and localhost share prod's build
  // env (same PostHog key), so without this they'd pollute the production
  // project. Bailing here also suppresses the consent banner off-prod.
  useEffect(() => {
    if (!isAnalyticsConfigured() || !isProductionAnalyticsHost()) return;
    initAnalytics();
    setShowBanner(!consentDecided());
    setEnabled(analyticsOptedIn());
  }, []);

  // SPA pageviews — only after opt-in.
  useEffect(() => {
    if (enabled) capturePageview(location.pathname);
  }, [enabled, location.pathname]);

  // Identify pseudonymously on login; reset on logout. Never sends email/PII.
  useEffect(() => {
    if (!enabled) return;
    if (currentUser?.uid) {
      identifyUser(currentUser.uid, { role: currentUser.role });
    } else {
      resetAnalytics();
    }
  }, [enabled, currentUser?.uid, currentUser?.role]);

  // Group by organization so events can be analysed per club/school.
  useEffect(() => {
    if (enabled && organization?.id) {
      groupOrg(organization.id, { name: organization.name, type: organization.type });
    }
  }, [enabled, organization?.id, organization?.name, organization?.type]);

  const handleAccept = () => {
    acceptAnalytics();
    setShowBanner(false);
    // Flipping `enabled` re-runs the effects above, which now fire the first
    // real pageview/identify/group with capturing on.
    setEnabled(true);
  };

  const handleDecline = () => {
    declineAnalytics();
    setShowBanner(false);
  };

  return (
    <>
      {children}
      {showBanner && <AnalyticsConsentBanner onAccept={handleAccept} onDecline={handleDecline} />}
    </>
  );
};
